import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Share, Heart, Ban, RotateCcw } from 'lucide-react';
import { SoftwareTalent } from '../types';
import { DomainType, saveTalent, unsaveTalent, discardTalent, restoreTalent, setActiveTenantId } from '../services/api';
import { deserializeFiltersFromUrl } from '../utils/urlFilters';
import DeveloperProfile from '../components/DeveloperProfile';
import { toast, Toaster } from '../components/Toast';
import { useTheme } from '../context/ThemeContext';
import { trackEvent, startTimedEvent } from '../utils/mixpanel';

const CandidateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    const urlTid = searchParams.get('tid');
    if (urlTid) {
      const parsed = Number(urlTid);
      if (!isNaN(parsed)) setActiveTenantId(parsed);
    } else {
      const storedTid = localStorage.getItem('tid');
      if (storedTid) {
        const params = new URLSearchParams(searchParams);
        params.set('tid', storedTid);
        navigate(`${location.pathname}?${params.toString()}`, { replace: true, state: location.state });
      }
    }
  }, [searchParams, navigate, location.pathname, location.state]);

  // Get domain from URL query params or navigation state
  const urlFilters = deserializeFiltersFromUrl();
  const filtersFromState = location.state?.filters;
  const domain: DomainType = (filtersFromState?.domain ?? urlFilters.domain ?? 'SWE') as DomainType;

  // Get navigation state from search results (if coming from search)
  const searchResultTalent = location.state?.talent as SoftwareTalent | undefined;

  const talentId = id ? Number(id) : null;

  // Save / Discard state
  const [isSaved, setIsSaved] = useState(false);
  const [isDiscarded, setIsDiscarded] = useState(false);
  const [actionLoading, setActionLoading] = useState<'save' | 'unsave' | 'discard' | 'restore' | null>(null);

  // DeveloperProfile fetches details and notifies via onDetailsLoaded
  const handleDetailsLoaded = useCallback((saved: boolean, discarded: boolean) => {
    setIsSaved(saved);
    setIsDiscarded(discarded);
  }, []);

  // Mixpanel: timed event for detail page engagement
  useEffect(() => {
    if (!talentId) return;
    trackEvent('Candidate Detail Viewed', { candidateId: talentId, domain });
    startTimedEvent('Candidate Detail Engagement');
    return () => {
      // On unmount, end the timed event — $duration is auto-attached
      trackEvent('Candidate Detail Engagement', { candidateId: talentId, domain });
    };
  }, [talentId, domain]);

  const handleBack = () => {
    if (location.state?.searchResults) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleShare = useCallback(() => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          toast.success('Link copied to clipboard');
        }).catch(() => {
          fallbackCopy(url);
        });
      } else {
        fallbackCopy(url);
      }
    } catch {
      fallbackCopy(url);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!talentId || actionLoading) return;
    try {
      if (isSaved) {
        setActionLoading('unsave');
        await unsaveTalent(talentId, domain);
        setIsSaved(false);
        toast.success('Candidate unsaved');
        trackEvent('Candidate Unsaved', { candidateId: talentId, domain });
      } else {
        setActionLoading('save');
        // If candidate is discarded, restore first before saving
        if (isDiscarded) {
          await restoreTalent(talentId, domain);
          setIsDiscarded(false);
        }
        await saveTalent(talentId, domain);
        setIsSaved(true);
        toast.success('Candidate saved');
        trackEvent('Candidate Saved', { candidateId: talentId, domain });
      }
    } catch {
      toast.error('Action failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  }, [talentId, domain, isSaved, isDiscarded, actionLoading]);

  const handleDiscard = useCallback(async () => {
    if (!talentId || actionLoading) return;
    try {
      if (isDiscarded) {
        setActionLoading('restore');
        await restoreTalent(talentId, domain);
        setIsDiscarded(false);
        toast.success('Candidate restored');
        trackEvent('Candidate Restored', { candidateId: talentId, domain });
      } else {
        setActionLoading('discard');
        // If candidate is saved, unsave first before discarding
        if (isSaved) {
          await unsaveTalent(talentId, domain);
          setIsSaved(false);
        }
        await discardTalent(talentId, domain);
        setIsDiscarded(true);
        toast.success('Candidate discarded');
        trackEvent('Candidate Discarded', { candidateId: talentId, domain });
      }
    } catch {
      toast.error('Action failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  }, [talentId, domain, isSaved, isDiscarded, actionLoading]);

  // Callback wrappers for DeveloperProfile
  const onSave = useCallback((tid: number) => {
    saveTalent(tid, domain).then(() => {
      setIsSaved(true);
      setIsDiscarded(false);
    }).catch(() => {});
  }, [domain]);

  const onUnsave = useCallback((tid: number) => {
    unsaveTalent(tid, domain).then(() => {
      setIsSaved(false);
    }).catch(() => {});
  }, [domain]);

  const onDiscard = useCallback((tid: number, d?: DomainType) => {
    discardTalent(tid, d || domain).then(() => {
      setIsDiscarded(true);
      setIsSaved(false);
    }).catch(() => {});
  }, [domain]);

  const onRestore = useCallback((tid: number, d?: DomainType) => {
    restoreTalent(tid, d || domain).then(() => {
      setIsDiscarded(false);
    }).catch(() => {});
  }, [domain]);

  if (!talentId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-4">Invalid Candidate ID</h2>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <Toaster />

      {/* Sticky top navbar */}
      <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back button */}
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to search</span>
            </button>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Share */}
              <button
                onClick={handleShare}
                className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                title="Copy share link"
              >
                <Share className="w-4 h-4" />
              </button>

              {/* Save / Unsave */}
              <button
                onClick={handleSave}
                disabled={!!actionLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isSaved ? 'font-semibold' : 'font-medium'}`}
                style={{
                  backgroundColor: isDark
                    ? (isSaved ? 'rgba(127, 29, 29, 0.3)' : 'rgba(127, 29, 29, 0.15)')
                    : (isSaved ? '#fef2f2' : '#fff5f5'),
                  color: isDark ? '#f87171' : '#ef4444',
                  border: `1px solid ${isDark ? '#f87171' : '#ef4444'}`,
                }}
              >
                {actionLoading === 'save' || actionLoading === 'unsave' ? (
                  <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Heart className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                )}
                {isSaved ? 'Saved' : 'Save'}
              </button>

              {/* Discard / Restore */}
              <button
                onClick={handleDiscard}
                disabled={!!actionLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDiscarded
                    ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-950/50'
                    : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                {actionLoading === 'discard' || actionLoading === 'restore' ? (
                  <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : isDiscarded ? (
                  <RotateCcw className="w-4 h-4" />
                ) : (
                  <Ban className="w-4 h-4" />
                )}
                {isDiscarded ? 'Restore' : 'Discard'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-5xl mx-auto">
        <div className="px-4 py-4 md:px-6 md:py-6">
          <DeveloperProfile
            talentId={talentId}
            domain={domain}
            onBack={handleBack}
            searchTalent={searchResultTalent}
            allTalent={[]}
            onDetailsLoaded={handleDetailsLoaded}
            onSave={onSave}
            onUnsave={onUnsave}
            onDiscard={onDiscard}
            onRestore={onRestore}
            isSaved={isSaved}
            isDiscarded={isDiscarded}
            hideHeader={true}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 flex flex-col items-center gap-3">
        <svg viewBox="0 0 76 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-auto text-zinc-400 dark:text-slate-600">
          <path d="M37.673 0c1.715 0 3.368 0.018 5.022 0.06 -0.006 0.004 -1.074 0.303 -1.445 0.377C25.207 0.104 11.212 2.563 11.212 5.389c0 2.945 14.922 5.333 33.329 5.333 13.714 0 25.492 -1.327 30.608 -3.22 -1.324 3.653 -17.596 6.541 -37.477 6.541 -3.606 0 -7.093 -0.095 -10.394 -0.272 8.132 3.627 14.933 8.388 14.933 13.801 0 0.04 0.04 8.662 0.04 19.477 -0.113 0 -0.28 -2.225 -0.28 -2.225C40.164 10.308 -2.646 16.47 0.129 6.699 1.2 2.928 17.763 0 37.673 0" fill="currentColor" />
        </svg>
        <p className="text-center text-xs text-zinc-400 dark:text-slate-600">
          &copy; {new Date().getFullYear()} Turing Enterprises Inc.
        </p>
      </div>
    </div>
  );
};

function fallbackCopy(text: string) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
    toast.success('Link copied to clipboard');
  } catch {
    toast.error('Failed to copy link');
  }
  document.body.removeChild(textarea);
}

export default CandidateDetail;
